import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, Loader2, Save } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormBuilder,
  Input,
  Label,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Switch,
  Textarea,
  toast,
} from "@eduviaos/ui-kit";
import { AIAssistDrawer, LastSuggestionPanel, PromptInputButton } from "@eduviaos/capability-ai-ui";
import { ApiError } from "@eduviaos/shared";
import type { CapabilityPromptContextWire } from "@eduviaos/shared";
import type { FormField } from "@eduviaos/shared/types";
import { formsKeys, useForm, useUpdateForm } from "@/hooks/useForms";
import { useCanEditForms } from "@/hooks/useFormsAuthoring";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import { useIaCapabilityAiAssist } from "@/hooks/useCapabilityAiAssist";
import { TenantFormsPausedBanner } from "@/components/TenantFormsPausedBanner";

interface MetaState {
  name: string;
  purpose: string;
  submit_button_label: string;
  success_message: string;
  requires_auth: boolean;
}

/**
 * Phase F-finale-5 Slice 5a — IA tenant-self form editor.
 *
 * The IA counterpart to the SA `TenantFormEditorPage`. Lives at
 * `/forms/:formSlug/edit` and is the only surface where a tenant admin
 * edits their own form fields. Tenant is resolved BE-side from the
 * `X-Tenant-Slug` header + JWT, so it hits the bare `/forms/{slug}`
 * routes (NOT the SA cross-tenant routes).
 *
 * Wires:
 *   - GET   /forms/{slug}  → hydrate (version-aware re-seed)
 *   - PATCH /forms/{slug}  → save (mints a new active version)
 *
 * Write affordances are gated on `useCanEditForms()`: when the SA has
 * paused editing (`pausedByTenant`) the page renders read-only with a
 * banner; saving and the L4 AI-assist trigger stay hidden.
 *
 * Slice 5b — the L4 (form field) AI-assist entry point: a header trigger
 * (gated behind a clean buffer) opens the shared `AIAssistDrawer` backed by
 * the self-scoped `/ai/capability` routes; applying mints a new version which
 * re-hydrates via the version-aware effect above.
 */
export function IaFormEditorPage() {
  const { formSlug = "" } = useParams<{ formSlug: string }>();
  const navigate = useNavigate();
  const { canEditForms, pausedByTenant } = useCanEditForms();

  const formQuery = useForm(formSlug);
  const updateMutation = useUpdateForm(formSlug);
  const qc = useQueryClient();
  const modules = useEnabledModules();

  // Slice 5b — L4 (form field) AI assist. Self-scoped client (no tenant in the
  // path; the IA `api` singleton's X-Tenant-Slug header is the authority). The
  // context is rebuilt at request time so it reflects the latest server draft.
  const buildAiContext = React.useCallback(
    (): CapabilityPromptContextWire => ({
      tenant_id: formQuery.data?.tenant_id ?? "",
      vertical: modules.vertical ?? "",
      enabled_modules: [...modules.enabledKeys],
      assessment_template_slugs: [],
      existing_stages: [],
      available_form_slugs: [formSlug],
      draft_version: 0,
    }),
    [formQuery.data?.tenant_id, modules.vertical, modules.enabledKeys, formSlug],
  );

  const onAiApplied = React.useCallback(() => {
    void qc.invalidateQueries({ queryKey: formsKeys.detail(formSlug) });
    void qc.invalidateQueries({ queryKey: formsKeys.list() });
    toast.success("AI field added — saved as a new form version.");
  }, [qc, formSlug]);

  const fieldAssist = useIaCapabilityAiAssist({
    targetKind: "field",
    buildContext: buildAiContext,
    onApplied: onAiApplied,
  });

  const [meta, setMeta] = React.useState<MetaState>({
    name: "",
    purpose: "custom",
    submit_button_label: "Submit",
    success_message: "Thank you. Your response has been recorded.",
    requires_auth: true,
  });
  const [fields, setFields] = React.useState<FormField[]>([]);
  const [hydrated, setHydrated] = React.useState(false);
  const [savedFlash, setSavedFlash] = React.useState(false);

  // Version-aware (re-)hydration: seed buffers on first load AND whenever
  // the server `version` changes (e.g. after a save mints v+1). Mirrors
  // the SA editor so a future AI-apply (Slice 5b) re-seeds the new field.
  const syncedVersionRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    const data = formQuery.data;
    if (!data || syncedVersionRef.current === data.version) return;
    syncedVersionRef.current = data.version;
    setMeta({
      name: data.name,
      purpose: data.purpose,
      submit_button_label: data.submit_button_label,
      success_message: data.success_message,
      requires_auth: data.requires_auth,
    });
    setFields(data.fields.slice().sort((a, b) => a.order - b.order));
    setHydrated(true);
  }, [formQuery.data]);

  const onSave = async () => {
    try {
      await updateMutation.mutateAsync({
        name: meta.name,
        purpose: meta.purpose,
        submit_button_label: meta.submit_button_label,
        success_message: meta.success_message,
        requires_auth: meta.requires_auth,
        fields,
      });
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 1400);
    } catch {
      // Surfaced below from updateMutation.error
    }
  };

  if (formQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="text-muted-foreground size-5 animate-spin" aria-hidden="true" />
      </div>
    );
  }

  if (formQuery.isError || !formQuery.data) {
    const message =
      formQuery.error instanceof ApiError
        ? formQuery.error.message
        : ((formQuery.error as Error | null)?.message ?? "Could not load form.");
    return (
      <div className="grid gap-3 py-12 text-center">
        <p className="text-destructive text-sm">{message}</p>
        <Link to="/forms" className="text-sm underline">
          Back to forms
        </Link>
      </div>
    );
  }

  const data = formQuery.data;
  const readOnly = !canEditForms;
  const dirty =
    hydrated &&
    !readOnly &&
    (meta.name !== data.name ||
      meta.purpose !== data.purpose ||
      meta.submit_button_label !== data.submit_button_label ||
      meta.success_message !== data.success_message ||
      meta.requires_auth !== data.requires_auth ||
      JSON.stringify(fields) !==
        JSON.stringify(data.fields.slice().sort((a, b) => a.order - b.order)));

  const submitting = updateMutation.isPending;
  const canSave = meta.name.trim().length > 0 && fields.length > 0 && !submitting && dirty;

  // Gate AI assist behind a clean buffer: applying mints v+1 server-side and the
  // editor re-hydrates from it, which would silently clobber an unsaved local edit.
  const startFieldAssist = () => {
    if (dirty) {
      toast.error("Save or discard your unsaved changes before using AI assist.");
      return;
    }
    fieldAssist.start();
  };

  const errorMessage =
    updateMutation.error instanceof ApiError
      ? updateMutation.error.message
      : (updateMutation.error as Error | undefined)?.message;

  return (
    <div className="grid gap-5">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderHeading>
            <PageHeaderTitle>{meta.name || data.name}</PageHeaderTitle>
            <PageHeaderDescription>
              Form <code className="bg-muted rounded px-1.5 py-0.5 text-xs">{formSlug}</code> ·
              saving spawns a new active version; the previous one is preserved in history.
            </PageHeaderDescription>
          </PageHeaderHeading>
          <PageHeaderActions>
            <Badge variant={data.requires_auth ? "default" : "success"}>
              {data.requires_auth ? "internal" : "public"}
            </Badge>
            <Badge variant="warn">v{data.version} active</Badge>
            {!readOnly && (
              <PromptInputButton
                onClick={startFieldAssist}
                label="AI assist"
                variant="outline"
                disabled={modules.isLoading}
                data-testid="ai-assist-field"
              />
            )}
            {!readOnly && (
              <Button onClick={onSave} disabled={!canSave} data-testid="form-save">
                {submitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                    Saving…
                  </>
                ) : savedFlash ? (
                  <>
                    <CheckCircle2 className="size-4" aria-hidden="true" />
                    Saved as v{updateMutation.data?.version ?? data.version}
                  </>
                ) : (
                  <>
                    <Save className="size-4" aria-hidden="true" />
                    Save new version
                  </>
                )}
              </Button>
            )}
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      {pausedByTenant ? <TenantFormsPausedBanner /> : null}

      {!readOnly && (
        <LastSuggestionPanel
          suggestion={fieldAssist.open ? null : fieldAssist.lastSuggestion}
          onReopen={fieldAssist.reopen}
        />
      )}

      {errorMessage && (
        <div
          role="alert"
          className="border-destructive/40 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2.5 text-sm"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>{errorMessage}</span>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Metadata</CardTitle>
          <CardDescription>
            Display name, submit button, and success message shown to end-users when this form is
            submitted.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="grid gap-1.5">
            <Label htmlFor="meta-name">Name</Label>
            <Input
              id="meta-name"
              data-testid="meta-name"
              value={meta.name}
              disabled={readOnly}
              onChange={(e) => setMeta((m) => ({ ...m, name: e.target.value }))}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="meta-purpose">Purpose</Label>
            <Input
              id="meta-purpose"
              value={meta.purpose}
              disabled={readOnly}
              onChange={(e) => setMeta((m) => ({ ...m, purpose: e.target.value }))}
              placeholder="custom, admission_enquiry, exam_attempt..."
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="meta-submit">Submit button label</Label>
            <Input
              id="meta-submit"
              value={meta.submit_button_label}
              disabled={readOnly}
              onChange={(e) => setMeta((m) => ({ ...m, submit_button_label: e.target.value }))}
            />
          </div>
          <div className="grid gap-1.5 md:col-span-2">
            <Label htmlFor="meta-success">Success message</Label>
            <Textarea
              id="meta-success"
              rows={2}
              value={meta.success_message}
              disabled={readOnly}
              onChange={(e) => setMeta((m) => ({ ...m, success_message: e.target.value }))}
            />
          </div>
          <label className="border-border bg-muted/30 flex cursor-pointer items-center justify-between gap-3 rounded-md border px-3 py-2 md:col-span-2">
            <div className="grid gap-0.5">
              <span className="text-sm font-medium">Require authentication</span>
              <span className="text-muted-foreground text-xs">
                When this is off, anyone with the form link can submit it.
              </span>
            </div>
            <Switch
              checked={meta.requires_auth}
              disabled={readOnly}
              onCheckedChange={(v) => setMeta((m) => ({ ...m, requires_auth: !!v }))}
            />
          </label>
        </CardContent>
      </Card>

      {readOnly ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Fields</CardTitle>
            <CardDescription>Read-only — editing is paused for your institution.</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="divide-border/60 divide-y text-sm">
              {fields.map((f) => (
                <li key={f.key} className="flex items-center justify-between py-2">
                  <span className="font-medium">{f.label}</span>
                  <code className="text-muted-foreground text-xs">{f.field_type}</code>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : (
        <FormBuilder fields={fields} onChange={setFields} />
      )}

      {dirty && (
        <div className="sticky inset-x-0 bottom-4 z-10">
          <div className="bg-card mx-auto flex max-w-2xl items-center justify-between rounded-md border px-4 py-2.5 shadow-lg">
            <p className="text-sm">
              <Badge variant="warn" className="mr-2 text-[10px]">
                Unsaved
              </Badge>
              Saving spawns <strong>v{data.version + 1}</strong> as the new active version.
            </p>
            <Button onClick={onSave} disabled={!canSave} size="sm">
              {submitting ? (
                <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <Save className="size-3.5" aria-hidden="true" />
              )}
              Save
            </Button>
          </div>
        </div>
      )}

      {!dirty && !readOnly && (
        <p className="text-muted-foreground text-center text-xs">
          Cancel by{" "}
          <button type="button" className="underline" onClick={() => navigate("/forms")}>
            navigating back
          </button>
          .
        </p>
      )}

      {!readOnly && (
        <AIAssistDrawer
          open={fieldAssist.open}
          onOpenChange={fieldAssist.setOpen}
          targetKind="field"
          state={fieldAssist.state}
          onGenerate={fieldAssist.generate}
          onApply={fieldAssist.apply}
          onReject={fieldAssist.reject}
          onRetry={fieldAssist.retry}
        />
      )}
    </div>
  );
}
